import React, { PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { gql } from '@yi/core';

import { analytics } from '~/common';
import { useCurrentUser } from '~/src/common/user';

import { MenuListButton } from './MenuListButton';
import { useMyLive } from '../hooks';
import { useLocale } from '../LocaleProvider';

type LiveContent = gql.typesClient.Fragment_LiveCard['content'] & {
  registrant_id?: string | null;
};

interface Props {
  contentId: string;
  liveContent?: LiveContent;
  displayIcon?: boolean;
}

// @TODO missing phrase import { i18n_unregister } from '@yi/i18n/phrases/i18n_unregister';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const i18n_unregister = (_: any) => 'Unregister';

export const UnregisterLiveButton: React.FC<PropsWithChildren<Props>> = ({
  contentId,
  liveContent,
  displayIcon,
}) => {
  const { locale } = useLocale();
  const { currentUser } = useCurrentUser();
  const { myLiveQuery } = useMyLive({ lazy: true });

  const [mutateUnregisterLive, { loading }] = useMutation<
    gql.typesClient.Mutate_DeleteLiveRegistration,
    gql.typesClient.Mutate_DeleteLiveRegistrationVariables
  >(gql.Mutate_DeleteLiveRegistration, {
    onCompleted: data => {
      const { deleteLiveRegistration } = data;
      if (deleteLiveRegistration?.status_code === 204) {
        myLiveQuery?.();
      }
    },
  });
  if (!liveContent) return null;

  return (
    <MenuListButton
      onClick={e => {
        e.preventDefault();
        if (loading) return;
        if (liveContent?.live_zoom) {
          const { live_zoom, registrant_id } = liveContent;
          if (!live_zoom) return;
          const { zoom_id, zoom_type } = live_zoom;
          const variables = {
            content_id: contentId,
            zoom_id: parseInt(zoom_id, 10),
            zoom_type,
            registrant_id: registrant_id || '',
          };
          mutateUnregisterLive({
            variables,
          });
          const cancelationToken = `${currentUser?.id}${contentId}`;
          analytics.sendLiveCancelation(cancelationToken);
        }
      }}
      loading={loading}
      Icon={<HighlightOffIcon />}
      displayIcon={displayIcon}
      data-testid="unregister-live-button"
      aria-label="Unregister live"
      buttonText={i18n_unregister(locale)}
    />
  );
};
